import { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/lib/lang";
import { getCaseDraftSuggestions } from "@/lib/api";
import DraftPane from "@/components/DraftPane";
import { Button } from "@/components/ui/button";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function CaseDraftPane({ caseId, caseData, model, defaultJurisdiction = "", onRefresh }) {
  const { t } = useLang();
  const [suggestions, setSuggestions] = useState(caseData?.draft_suggestions || []);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestedForm, setSuggestedForm] = useState(null);
  const [formKey, setFormKey] = useState(0);

  const caseDrafts = useMemo(
    () => (caseData?.documents || []).filter((d) => d.role === "draft"),
    [caseData?.documents],
  );

  const loadSuggestions = useCallback(
    async (refresh = false) => {
      setLoadingSuggestions(true);
      try {
        const data = await getCaseDraftSuggestions(caseId, model, refresh);
        setSuggestions(data.suggestions || []);
        onRefresh?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || t("case_draft_suggestions_failed"));
        if (refresh) setSuggestions(caseData?.draft_suggestions || []);
      } finally {
        setLoadingSuggestions(false);
      }
    },
    [caseId, model, t, onRefresh, caseData?.draft_suggestions],
  );

  useEffect(() => {
    setSuggestions(caseData?.draft_suggestions || []);
  }, [caseData?.draft_suggestions]);

  useEffect(() => {
    if ((caseData?.draft_suggestions || []).length === 0) {
      loadSuggestions(false);
    }
    // Only auto-generate when opening a case with no cached suggestions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const applySuggestion = (item) => {
    setSuggestedForm({
      doc_type: item.doc_type,
      instructions: item.instructions,
      parties: item.parties || "",
      jurisdiction: item.jurisdiction || "",
      _key: Date.now(),
    });
    setFormKey((k) => k + 1);
    toast.success(t("case_draft_use_suggestion"));
  };

  const handleDraftSaved = () => {
    onRefresh?.();
  };

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="case-draft-pane">
      <div className="grid-border-b bg-slate-50/50 px-6 py-4">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-slate-900" strokeWidth={1.6} />
            <h3 className="font-serif-display text-base text-slate-900">
              {t("case_draft_suggestions")}
            </h3>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => loadSuggestions(true)}
            disabled={loadingSuggestions}
            className="rounded-none h-8 text-[11px] gap-1.5 border-slate-300"
            data-testid="case-draft-suggestions-refresh"
          >
            {loadingSuggestions ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <RefreshCw size={12} />
            )}
            {t("case_draft_suggestions_refresh")}
          </Button>
        </div>

        {loadingSuggestions && suggestions.length === 0 ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 py-2">
            <Loader2 size={14} className="animate-spin" />
            {t("streaming")}
          </div>
        ) : suggestions.length === 0 ? (
          <p className="text-xs text-slate-400">{t("case_draft_suggestions_empty")}</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {suggestions.map((item, idx) => (
              <button
                key={`${item.doc_type}-${idx}`}
                type="button"
                data-testid={`case-draft-suggestion-${idx}`}
                onClick={() => applySuggestion(item)}
                className="min-w-[220px] max-w-[280px] shrink-0 text-start grid-border bg-white p-3 hover:bg-slate-50 transition-colors"
              >
                <span className="inline-block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                  {t(`types.${item.doc_type}`) || item.doc_type}
                </span>
                <span className="block text-sm font-medium text-slate-900 line-clamp-2">
                  {item.title}
                </span>
                {item.rationale && (
                  <span className="block text-xs text-slate-500 mt-1.5 line-clamp-2">
                    {item.rationale}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <DraftPane
          key={formKey}
          model={model}
          caseId={caseId}
          drafts={caseDrafts}
          headerTitle={t("case_draft")}
          suggestedForm={suggestedForm}
          defaultJurisdiction={defaultJurisdiction}
          onDraftSaved={handleDraftSaved}
          onDraftUpdated={handleDraftSaved}
        />
      </div>
    </div>
  );
}
